import { create, type StateCreator } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { Platform } from 'react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { Scope, normalizeAgeRange } from '@utils'
import { ListingContactsFilters } from '../types'

// Interface pour l'état du store listingContacts
interface ListingContactsState {
  // États de l'interface
  searchText: string
  scope: Scope[]
  
  // Filtres locaux (ceux d'AddContacts ont leur propre store, plus bas)
  localFilters: ListingContactsFilters
  
  // Actions pour l'interface
  setSearchText: (text: string) => void
  setScope: (scope: Scope[]) => void
  
  // Actions pour les filtres locaux
  setLocalFilters: (filters: Partial<ListingContactsFilters>) => void
  resetLocalFilters: () => void
  
  // Actions utilitaires
  reset: () => void
  
  // Getters pour les filtres
  hasLocalFilters: () => boolean
  getActiveFiltersCount: () => number
  getFilterSummary: () => string
  
  // Actions de suppression des filtres (comme dans AddContacts)
  clearOrigins: () => void
  clearResidence: () => void
  clearLivedPlaces: () => void
  clearNationalities: () => void
  clearProfessions: () => void
  clearHashtags: () => void
  clearContexts: () => void
  clearEmotionalContexts: () => void
  clearRelationshipStatus: () => void
  clearRelationalColor: () => void
  clearLanguages: () => void
  clearCategories: () => void
  clearCurrentPosition: () => void
  clearDegree: () => void
  clearAllFilters: () => void
}

// Filtres par défaut pour ListingContacts (vide par défaut)
const defaultFilters: ListingContactsFilters = {
  searchText: '',
  scope: [Scope.PRIVATE],
  residenceContinents: [],
  residenceCountries: [],
  residenceRegions: [],
  residence: [],
  livedPlaces: [],
  origins: [],
  nationalities: [],
  professions: [],
  hashtags: [],
  contexts: [],
  emotionalContexts: [],
  relationshipStatus: [],
  relationalColor: [],
  languages: [],
  categories: [],
  location: {},
  degree: null as number | null,
}

const createListingContactsState: StateCreator<ListingContactsState> = (set, get) => ({
      // État initial
      searchText: '',
      scope: [Scope.PRIVATE],
      localFilters: defaultFilters,
      
      // Actions pour l'interface
      setSearchText: (text) => set({ searchText: text }),
      
      setScope: (scope: Scope[]) => set({ scope }),
      
      // Actions pour les filtres locaux
      setLocalFilters: (filters) => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, ...filters } })
      },
      
      resetLocalFilters: () => set({ localFilters: defaultFilters }),
      
      // Actions utilitaires
      reset: () => set({
        searchText: '',
        scope: [Scope.PRIVATE],
        localFilters: defaultFilters
      }),
      
      // Getters pour les filtres
      hasLocalFilters: () => {
        const { localFilters } = get()
        return (
          localFilters.searchText.trim().length > 0 ||
          (localFilters.residence && localFilters.residence.length > 0) ||
          (localFilters.residenceContinents && localFilters.residenceContinents.length > 0) ||
          (localFilters.residenceCountries && localFilters.residenceCountries.length > 0) ||
          (localFilters.residenceRegions && localFilters.residenceRegions.length > 0) ||
          (localFilters.livedPlaces && localFilters.livedPlaces.length > 0) ||
          (localFilters.origins && localFilters.origins.length > 0) ||
          (localFilters.nationalities && localFilters.nationalities.length > 0) ||
          (localFilters.professions && localFilters.professions.length > 0) ||
          (localFilters.hashtags && localFilters.hashtags.length > 0) ||
          (localFilters.contexts && localFilters.contexts.length > 0) ||
          (localFilters.emotionalContexts && localFilters.emotionalContexts.length > 0) ||
          (localFilters.relationshipStatus && localFilters.relationshipStatus.length > 0) ||
          (localFilters.relationalColor && localFilters.relationalColor.length > 0) ||
          (localFilters.languages && localFilters.languages.length > 0) ||
          (localFilters.categories && localFilters.categories.length > 0) ||
          (localFilters.location && Object.values(localFilters.location).some(value => value !== undefined)) ||
          (localFilters.gender !== undefined) ||
          (normalizeAgeRange(localFilters.ageRange) !== undefined) ||
          (localFilters.degree != null)
        )
      },
      
      getActiveFiltersCount: () => {
        const { localFilters } = get()
        let count = 0
        if (
          (localFilters.residence && localFilters.residence.length > 0) ||
          (localFilters.residenceContinents && localFilters.residenceContinents.length > 0) ||
          (localFilters.residenceCountries && localFilters.residenceCountries.length > 0) ||
          (localFilters.residenceRegions && localFilters.residenceRegions.length > 0)
        ) {
          count++
        }
        if (localFilters.livedPlaces && localFilters.livedPlaces.length > 0) count++
        if (localFilters.origins && localFilters.origins.length > 0) count++
        if (localFilters.nationalities && localFilters.nationalities.length > 0) count++
        if (localFilters.professions && localFilters.professions.length > 0) count++
        if (localFilters.hashtags && localFilters.hashtags.length > 0) count++
        if (localFilters.contexts && localFilters.contexts.length > 0) count++
        if (localFilters.emotionalContexts && localFilters.emotionalContexts.length > 0) count++
        if (localFilters.relationshipStatus && localFilters.relationshipStatus.length > 0) count++
        if (localFilters.relationalColor && localFilters.relationalColor.length > 0) count++
        if (localFilters.languages && localFilters.languages.length > 0) count++
        if (localFilters.categories && localFilters.categories.length > 0) count++
        if (localFilters.location && Object.values(localFilters.location).some(value => value !== undefined)) count++
        if (localFilters.gender !== undefined) count++
        if (normalizeAgeRange(localFilters.ageRange) !== undefined) count++
        if (localFilters.degree != null) count++
        return count
      },
      
      getFilterSummary: () => {
        const count = get().getActiveFiltersCount()
        return count > 0 ? `${count} filtre${count > 1 ? 's' : ''} actif${count > 1 ? 's' : ''}` : ''
      },
      
      clearResidence: () => {
        const { localFilters } = get()
        set({
          localFilters: {
            ...localFilters,
            residence: [],
            residenceContinents: [],
            residenceCountries: [],
            residenceRegions: [],
          },
        })
      },

      clearLivedPlaces: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, livedPlaces: [] } })
      },

      clearOrigins: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, origins: [] } })
      },

      clearNationalities: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, nationalities: [] } })
      },
      
      clearProfessions: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, professions: [] } })
      },
      
      clearHashtags: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, hashtags: [] } })
      },

      clearContexts: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, contexts: [] } })
      },

      clearEmotionalContexts: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, emotionalContexts: [] } })
      },

      clearRelationshipStatus: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, relationshipStatus: [] } })
      },

      clearRelationalColor: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, relationalColor: [] } })
      },

      clearLanguages: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, languages: [] } })
      },

      clearCategories: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, categories: [] } })
      },
      
      clearCurrentPosition: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, location: {} } })
      },

      clearDegree: () => {
        const { localFilters } = get()
        set({ localFilters: { ...localFilters, degree: null } })
      },

      clearAllFilters: () => {
        set({ localFilters: defaultFilters })
      }
})

// Store Zustand avec persistance pour ListingContacts
export const useListingContactsStore = create<ListingContactsState>()(
  persist(
    createListingContactsState,
    {
      name: 'listing-contacts-storage',
      storage: createJSONStorage(() => AsyncStorage),
      // Ne persister que certaines données
      partialize: (state) => ({
        scope: state.scope,
        localFilters: state.localFilters
      }),
      // Migration pour assurer la compatibilité avec les anciennes données
      migrate: (persistedState: any, version: number) => {
        if (persistedState && persistedState.localFilters) {
          // S'assurer que toutes les nouvelles propriétés existent
          persistedState.localFilters = {
            ...defaultFilters,
            ...persistedState.localFilters,
            languages: persistedState.localFilters.languages || [],
            categories: persistedState.localFilters.categories || [],
            degree: persistedState.localFilters.degree ?? null,
            residenceContinents: persistedState.localFilters.residenceContinents || [],
            residenceCountries: persistedState.localFilters.residenceCountries || [],
            residenceRegions: persistedState.localFilters.residenceRegions || [],
          }
          // Until version 1 the web slider saved its two ends as the range
          // 1-100, which hid the people without a birth date although the
          // slider looked untouched: its two ends are no age filter.
          const savedAge = persistedState.localFilters.ageRange
          if (version < 2 && Platform.OS === 'web' && savedAge?.[0] === '1' && savedAge?.[1] === '100') {
            persistedState.localFilters.ageRange = undefined
          }
        }
        return persistedState
      },
      version: 2
    }
  )
)

// Filters of « Ajouter un contact »: same shape, their own memory, so a filter
// chosen in Contacts does not narrow the suggestions (and the reverse). Not
// saved on the device: they start empty at each launch.
export const useSuggestedContactsFiltersStore = create<ListingContactsState>()(createListingContactsState)
